import React, { useState } from 'react';
import { useCart } from '../context/CartProvider';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Nav from '../components/nav/Nav';
import Footer from '../components/footer/Footer';
import './CheckoutPage.css';

// 1. Import the Library
import {
  CitySelect,
  CountrySelect,
  StateSelect,
} from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";

function CheckoutPage() {
  const { cart, clearCart } = useCart();
  const navigate = useNavigate();

  // ID states for the library filtering
  const [countryid, setCountryid] = useState(0);
  const [stateid, setStateid] = useState(0);

  // Toggle for manual city entry
  const [isManualCity, setIsManualCity] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '', // This will store the ISO code (e.g., 'ES')
    phone: '',
  });

  const [error, setError] = useState('');

  // --- 2. SHIPPING CALCULATION LOGIC ---
  const subtotal = cart.reduce((total, item) => total + item.price * item.quantity, 0);

  let shippingCost = 0;

  // We check the ISO code stored in formData.country
  if (formData.country === 'ES') {
    // Spain Logic
    if (subtotal < 50) {
      shippingCost = 4.99;
    } else {
      shippingCost = 0; // Free
    }
  } else if (formData.country !== '') {
    // International Logic (Any other code)
    if (subtotal < 70) {
      shippingCost = 12.99;
    } else {
      shippingCost = 0; // Free
    }
  }

  const finalTotal = subtotal + shippingCost;
  // --- END CALCULATION ---

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhoneKey = (e) => {
      const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '+', ' ', '-', '(', ')'];
      if (e.ctrlKey || e.metaKey || e.key === 'Backspace' || e.key === 'Delete' || e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Tab') return;
      if (!allowedKeys.includes(e.key)) e.preventDefault();
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setError('');

    const orderData = {
      full_name: formData.full_name,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      postal_code: formData.postal_code,
      country: formData.country,
      phone: formData.phone,
      items: cart.map(item => ({
        product: item.id,
        quantity: item.quantity,
        price: item.price
      }))
    };

    const config = {
      headers: {
        'Authorization': `Token ${localStorage.getItem('authToken')}`
      }
    };

    try {
      const orderResponse = await axios.post('/api/v1/checkout/', orderData, config);
      const order_id = orderResponse.data.id;

      const stripeResponse = await axios.post(
        `/api/v1/create-checkout-session/${order_id}/`,
        { },
        config
      );

      window.location.href = stripeResponse.data.checkout_url;

    } catch (err) {
      console.error('Checkout failed:', err);
      let friendlyError = 'An unexpected error occurred. Please try again.';
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        if (errorData.error) friendlyError = errorData.error;
        else if (errorData.detail) friendlyError = errorData.detail;
        else {
          const firstErrorKey = Object.keys(errorData)[0];
          friendlyError = `${firstErrorKey}: ${errorData[firstErrorKey][0]}`;
        }
      }
      setError(friendlyError);
    }
  };

  return (
    <>
      <Nav />
      <div className="checkout-page">
        <h2>Checkout</h2>
        <div className="checkout-container">

          <div className="cart-summary-column">
            <h3>Your Order</h3>
            {cart.map(item => (
              <div key={item.id} className="summary-item">
                <img src={item.get_image} alt={item.name} />
                <div className="summary-item-details">
                  <span>{item.name} (x{item.quantity})</span>
                  <span>€{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              </div>
            ))}
            <hr />

            {/* --- 3. SUMMARY DISPLAY --- */}
            <div className="summary-row" >
              <span>Subtotal</span>
              <span>€{subtotal.toFixed(2)}</span>
            </div>

            <div className="summary-row">
              <span>Shipping</span>
              <span>
                {shippingCost === 0 ? <span style={{color: '#008a20'}}>FREE</span> : `€${shippingCost.toFixed(2)}`}
              </span>
            </div>

            <div className="summary-total">
              <span>Total</span>
              <span>€{finalTotal.toFixed(2)}</span>
            </div>
            {/* --- END SUMMARY --- */}
          </div>

          <div className="shipping-form-column">
            <h3>Shipping Address</h3>
            <form onSubmit={handleCheckout}>

              <div className="form-group">
                <label htmlFor="full_name">Full Name</label>
                <input type="text" id="full_name" name="full_name" value={formData.full_name} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <input type="text" id="address" name="address" value={formData.address} onChange={handleChange} required />
              </div>

              {/* --- 4. LIBRARY DROPDOWNS --- */}
              <div className="form-group">
                <label>Country</label>
                <CountrySelect
                  onChange={(e) => {
                    setCountryid(e.id);
                    // We save the ISO2 Code (e.g. "ES") so our logic works
                    setFormData({ ...formData, country: e.iso2 });
                  }}
                  placeHolder="Select Country"
                />
              </div>

              <div className="form-group">
                <label>State / Province</label>
                <StateSelect
                  countryid={countryid}
                  onChange={(e) => {
                    setStateid(e.id);
                    setFormData({ ...formData, state: e.name });
                  }}
                  placeHolder="Select State"
                />
              </div>

              <div className="form-group">
                <label>City</label>
                {!isManualCity ? (
                  <div className="city-select-wrapper">
                    <CitySelect
                      countryid={countryid}
                      stateid={stateid}
                      onChange={(e) => {
                        setFormData({ ...formData, city: e.name });
                      }}
                      placeHolder="Select City"
                    />
                    <button
                        type="button"
                        className="text-link-button"
                        onClick={() => {
                            setIsManualCity(true);
                            setFormData({ ...formData, city: '' });
                        }}
                    >
                        Can't find your city? Type it manually
                    </button>
                  </div>
                ) : (
                  <div className="city-input-wrapper">
                    <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="Type your city name..."
                        required
                    />
                     <button
                        type="button"
                        className="text-link-button"
                        onClick={() => {
                            setIsManualCity(false);
                            setFormData({ ...formData, city: '' });
                        }}
                    >
                        Back to list
                    </button>
                  </div>
                )}
              </div>
              {/* --- END DROPDOWNS --- */}

              <div className="form-group">
                <label htmlFor="postal_code">Postal Code</label>
                <input type="text" id="postal_code" name="postal_code" value={formData.postal_code} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  onKeyDown={handlePhoneKey}
                />
              </div>

              {error && <p className="error-message">{error}</p>}

              <button type="submit" className="cp-pay-now-button">
                Pay Now
              </button>
            </form>
            <br></br>
            <h1>SHIPPING</h1>
            <p><strong>Spain: FREE </strong>on orders over <strong>50€</strong> or 4.99€</p>
            <p><strong>International: FREE </strong> on orders over <strong>70€</strong> or 12.99€
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default CheckoutPage;
